import { DEFAULT_POSITION_SELECTOR_CONFIG, flattenPositionConfig, MATCHPLAY_POSITION_SELECTOR_CONFIG, PositionSelectorConfig } from "@Constants/game/position-config";
import { getPositionsFromSection } from "@Constants/game/position-sections";
import { AnalyticsEvents } from "@Enums/index";
import PlayPositions from "@Enums/PlayPositions";
import FieldPositions from "@Enums/SimplifiedPlayPositions";
import StorageKeys from "@Enums/StorageKeys";
import { loadPositionsFromStorage, savePositionsToStorage } from "@Helpers/position-storage";
import { useAnalytics } from "@hooks/common/analytics/use-analytics";
import { useCallback, useEffect, useMemo, useState } from "react";

const EMPTY_POSITIONS: PlayPositions[] = [];
const normalizePosition = (position: PlayPositions) => position;

const areSamePositions = (left: PlayPositions[], right: PlayPositions[]) => left.length === right.length && left.every((position, index) => position === right[index]);

type UsePositionSelectionProps = {
  initialPositions?: PlayPositions[];
  onPositionsSelected?: (positions: PlayPositions[]) => void;
  onCancel?: () => void;
  selectorConfig?: PositionSelectorConfig;
  storageKey?: StorageKeys;
  normalizeStoredPosition?: (position: PlayPositions) => PlayPositions;
};

export const usePositionSelection = ({
  initialPositions = EMPTY_POSITIONS,
  onPositionsSelected,
  onCancel,
  selectorConfig = DEFAULT_POSITION_SELECTOR_CONFIG,
  storageKey = StorageKeys.SELECTED_POSITIONS,
  normalizeStoredPosition = normalizePosition,
}: UsePositionSelectionProps = {}) => {
  const { track } = useAnalytics();
  const availablePositions = useMemo(() => flattenPositionConfig(selectorConfig.positions).map(position => position.id), [selectorConfig.positions]);
  const getInitialPositions = useCallback(
    () =>
      (initialPositions.length > 0 ? initialPositions : loadPositionsFromStorage(storageKey))
        .map(normalizeStoredPosition)
        .filter(position => availablePositions.includes(position)),
    [availablePositions, initialPositions, normalizeStoredPosition, storageKey]
  );

  const [selectedPositions, setSelectedPositions] = useState<PlayPositions[]>(getInitialPositions);
  const [hoveredSection, setHoveredSection] = useState<FieldPositions | null>(null);

  useEffect(() => {
    const nextPositions = getInitialPositions();
    setSelectedPositions(currentPositions => (areSamePositions(currentPositions, nextPositions) ? currentPositions : nextPositions));
  }, [getInitialPositions]);

  const selectRandomPositions = () => {
    setSelectedPositions(EMPTY_POSITIONS);
    savePositionsToStorage(EMPTY_POSITIONS, storageKey);
    onPositionsSelected?.(EMPTY_POSITIONS);
    track(AnalyticsEvents.positions_selected, { positions: "random" });
  };

  const selectAllPositionsFromSection = (section: FieldPositions) => {
    const positions = getPositionsFromSection(section, selectorConfig.sections).filter(position => availablePositions.includes(position));
    const allSelected = positions.length > 0 && positions.every(position => selectedPositions.includes(position));

    setSelectedPositions(
      allSelected
        ? selectedPositions.filter(position => !positions.includes(position))
        : [...selectedPositions, ...positions.filter(position => !selectedPositions.includes(position))]
    );
  };

  const handleConfirm = () => {
    savePositionsToStorage(selectedPositions, storageKey);
    onPositionsSelected?.(selectedPositions);
    track(AnalyticsEvents.positions_selected, { positions: selectedPositions });
  };

  const handleCancel = () => {
    setSelectedPositions(getInitialPositions());
    setHoveredSection(null);
    onCancel?.();
  };

  return {
    selectedPositions,
    hoveredSection,
    setSelectedPositions: (positions: PlayPositions[]) => setSelectedPositions(positions.filter(position => availablePositions.includes(position))),
    selectRandomPositions,
    selectAllPositionsFromSection,
    handleConfirm,
    setHoveredSection,
    handleCancel,
  };
};

const normalizeMatchPlayStoredPosition = (position: PlayPositions) => {
  switch (position) {
    case PlayPositions.CENTER_BACK:
      return PlayPositions.LEFT_CENTER_BACK;
    case PlayPositions.CENTER_MIDFIELDER:
      return PlayPositions.LEFT_CENTER_MIDFIELDER;
    default:
      return position;
  }
};

export const useMatchPlayPositionSelection = () =>
  usePositionSelection({
    selectorConfig: MATCHPLAY_POSITION_SELECTOR_CONFIG,
    storageKey: StorageKeys.MATCHPLAY_SELECTED_POSITIONS,
    normalizeStoredPosition: normalizeMatchPlayStoredPosition,
  });
